import OneSignal from 'react-onesignal';

/**
 * Wrapper that lets us call OneSignal SDK methods without crashing on
 * environments where the SDK has not initialised, has a stale type
 * surface, or rejects asynchronously. We never block the UI on a OneSignal
 * call, so swallowing failures is safe: the worst-case is push delivery
 * is best-effort, which it always is.
 */
export const fireAndForget = <T,>(p: Promise<T> | T) =>
  Promise.resolve(p).catch(() => {});

/**
 * Fire the browser's native permission request synchronously, in-gesture.
 *
 * react-onesignal routes every SDK call (including `optIn()` and
 * `Notifications.requestPermission()`) through `OneSignalDeferred`, so the OS
 * permission request runs on a later tick, outside the click's transient user
 * activation. Firefox only shows the prompt when `Notification.requestPermission()`
 * is called during that activation window, so it silently skips it while Chrome
 * and Safari are lenient. Requesting natively here, from the same synchronous
 * call stack as the user gesture, makes the prompt appear on Firefox too. Only
 * requests when the state is `default`, so an already-granted second browser
 * still falls through to OneSignal's `optIn()` to subscribe.
 *
 * @returns A promise that resolves once the native request settles (or is skipped).
 */
const requestNativePermissionInGesture = (): Promise<unknown> => {
  if (typeof window === 'undefined' || !('Notification' in window)) return Promise.resolve();
  const native = window.Notification;
  if (native.permission !== 'default') return Promise.resolve();
  return fireAndForget(native.requestPermission());
};

const SDK_READY_POLL_MS = 250;
const SDK_READY_MAX_ATTEMPTS = 40;

type OptInSurface = { optIn?: () => Promise<void> } | undefined;

const readPushSubscription = (): OptInSurface => {
  try {
    return (OneSignal.User as { PushSubscription?: OptInSurface })?.PushSubscription;
  } catch {
    return undefined;
  }
};

/**
 * Resolve once `OneSignal.User.PushSubscription` exists, or give up.
 *
 * `OneSignal.init()` is fire-and-forget, so on a cold load the subscription
 * surface can still be missing when the user clicks. Calling `optIn()` through
 * optional chaining at that point evaluates to `undefined` and subscribes
 * nothing, with no error to catch - the prompt appears and the browser is
 * never registered.
 */
const waitForPushSubscription = async (
  attempts = SDK_READY_MAX_ATTEMPTS,
): Promise<OptInSurface> => {
  for (let i = 0; i < attempts; i += 1) {
    const sub = readPushSubscription();
    if (sub?.optIn) return sub;
    await new Promise(resolve => setTimeout(resolve, SDK_READY_POLL_MS));
  }
  return readPushSubscription();
};

/**
 * Subscribe THIS browser/device to web push. `optIn()` requests OS permission
 * when needed AND opts the OneSignal subscription in, so it works on a fresh
 * browser and re-subscribes one that was previously opted out. This is the key
 * to letting an already-enabled user turn on push in a second browser/device:
 * `Notifications.requestPermission()` alone no-ops once permission is granted,
 * which never subscribes them.
 *
 * Must be called synchronously from a user gesture handler: the native prompt
 * is fired first (see {@link requestNativePermissionInGesture}) so Firefox
 * shows it, then OneSignal's `optIn()` subscribes once the SDK is ready.
 *
 * @returns Resolves `true` when `optIn()` ran, `false` when the SDK never
 *   became available or the call failed. Callers can surface the failure
 *   instead of leaving the user with a toggle that silently stays off.
 */
export const optInPush = async (): Promise<boolean> => {
  await requestNativePermissionInGesture();
  const sub = await waitForPushSubscription();
  if (!sub?.optIn) return false;
  try {
    await sub.optIn();
    return true;
  } catch {
    return false;
  }
};
